<template>
    <b-overlay :show="flagShowOverlay">
        <div id="app" class="h-100" :class="[skinClasses]">
            <component :is="layout">
                <router-view />
            </component>
        </div>
        <ModalInsertAccountPinTemplate v-if="flagShowModalPin" :is-inative-modal="true"
            @resetInactivityTime="resetInactivityTime()" />
        <AiAssistantWidget />
        <template #overlay>
            <div class="text-center d-flex flex-column align-items-center">
                <load-spinner class="load-spinner-global" />
            </div>
        </template>
    </b-overlay>
</template>

<script>
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import { provideSignalR } from "@/plugins/signalr";
import useAppConfig from "@core/app-config/useAppConfig";
import { closeTooltips } from "@core/utils/utils";
import { useWindowSize, useCssVar } from "@vueuse/core";
import store from "@/store";
import { BOverlay } from "bootstrap-vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
import ModalInsertAccountPinTemplate from "@/@core/components/b-modal/ModalChangeAccount/ModalInsertAccountTemplate.vue";
import AiAssistantWidget from "@core/components/ai-assistant/AiAssistantWidget.vue";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () => import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
    components: {
        LoadSpinner: () => import("@/assets/images/pages/load-icon.svg"),
        LayoutHorizontal,
        LayoutVertical,
        LayoutFull,
        BOverlay,
        ModalInsertAccountPinTemplate,
        AiAssistantWidget
    },
    computed: {
        ...mapGetters("app", [
            "flagShowOverlay",
            "flagShowModalPin",
            "flagOverlayModalPin",
            "updateUnits"
        ]),
        ...mapGetters("auth", [
            "getPermissions",
            "getUserInfo"
        ]),

        layout() {
            if (this.$route.meta.layout === "full") return "layout-full";
            return `layout-${this.contentLayoutType}`;
        },
        contentLayoutType() {
            return this.$store.state.appConfig.layout.type;
        }
    },
    data() {
        return {
            timeoutFunction: null,
            timeoutOverlay: null,
            isFirstLoading: true,
            userEmail: "",
            prefix: this.$router.currentRoute.params.prefix
        };
    },
    watch: {
        $route(to, from) {
            this.inactivityTime();

            if (this.getPermissions.length > 0) {
                this.verifyCanNavigate(to);
            }
        },
        flagShowOverlay(value) {
            this.$router.currentRoute.path.includes("operator") && this.resetInactivityTime();
            const isChangeAccount = this.$router.currentRoute.name === "change-account";

            if (
                !value &&
                localStorage.getItem("blockScreen") === "true" &&
                this.isFirstLoading &&
                !isChangeAccount
            ) {
                this.isFirstLoading = false;
                this.UPDATE_FLAG_SHOW_MODAL_PIN(true);
            }
        },
    },

    beforeCreate() {
        const colors = ["primary", "secondary", "success", "info", "warning", "danger", "light", "dark"];
        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
        }
        const breakpoints = ["xs", "sm", "md", "lg", "xl"];
        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(
                useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2)
            );
        }
        const { isRTL } = $themeConfig.layout;
        document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
    },

    created() {
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const hasPcpAccess = userData.ability?.some(a => a.subject === "pcp" || (a.action === "manage" && a.subject === "all"));

        if (hasPcpAccess) {
            localStorage.removeItem("mode");
            localStorage.removeItem("lastModule");
            localStorage.removeItem("blockScreen");
        }

        closeTooltips(window);
        localStorage.setItem("requestList", JSON.stringify([]));

        const reloaded = localStorage.getItem("reloaded");
        if (reloaded !== "true") {
            localStorage.setItem("reloaded", "true");
            window.location.reload();
            return;
        }

        this.initializeApp();
    },

    mounted() {
        window.addEventListener("storage", this.handleStorageEvent);
    },

    beforeDestroy() {
        window.removeEventListener("storage", this.handleStorageEvent);
    },

    methods: {
        handleStorageEvent(event) {
            if (event.key === "userData" || event.key === "accessToken") {
                const newUserData = JSON.parse(localStorage.getItem("userData") || "{}");
                const stateUserData = this.$store.state.auth?.userInfo?.userData;
                if (newUserData.guid && stateUserData?.guid && newUserData.guid !== stateUserData.guid) {
                    window.location.reload();
                }
            }
            if (event.key === "blockScreen") {
                if (event.newValue === "true") {
                    this.addOverlayModalPinAndOverlayNavbar();
                    this.UPDATE_FLAG_SHOW_MODAL_PIN(true);
                } else if (event.newValue === "false") {
                    this.UPDATE_FLAG_SHOW_MODAL_PIN(false);
                    this.UPDATE_FLAG_OVERLAY_MODAL_PIN(false);
                    if (window.$) $("body").removeClass("stop-scrolling");
                }
            }
        },
        ...mapMutations("app", [
            "UPDATE_FLAG_SHOW_OVERLAY",
            "UPDATE_FLAG_SHOW_MODAL_PIN",
            "UPDATE_FLAG_OVERLAY_MODAL_PIN",
            "UPDATE_UNITS"
        ]),
        ...mapActions("auth", [
            "fetchPermissions",
            "updateUserInfoCookie"
        ]),

        async initializeApp() {
            this.UPDATE_FLAG_SHOW_OVERLAY(true);

            try {
                const cookieUserInfo = this.$cookies.get("userInfo");
                if (!cookieUserInfo && window.location.pathname.endsWith("/change-account") &&
                    JSON.parse(localStorage.getItem("loggedAccountsInfo") || "[]").length) {
                    this.$i18n.locale = localStorage.getItem("language") || this.$i18n.locale;
                    return;
                }
                if (cookieUserInfo && cookieUserInfo.userData) {
                    const localUserData = JSON.parse(localStorage.getItem("userData") || "{}");
                    if (localUserData.guid !== cookieUserInfo.userData.guid) {
                        localStorage.setItem("userData", JSON.stringify(cookieUserInfo.userData));
                        if (cookieUserInfo.temporaryToken || cookieUserInfo.accessToken) {
                            localStorage.setItem("accessToken", cookieUserInfo.temporaryToken || cookieUserInfo.accessToken);
                        }
                    }
                }

                await this.fetchPermissions();

                this.verifyConnection();

                this.verifyCanNavigate(this.$router.currentRoute);

            } catch (error) {
                console.error("Falha crítica na inicialização:", error);
            } finally {
                setTimeout(() => {
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }, 300);
            }
        },

        verifyConnection(v) {
            const userInfo = this.getUserInfo;
            const permissions = this.getPermissions;
            const company = this.$cookies.get("company");

            if (!userInfo || !permissions.length) return;

            const hasPermission = permissions.find((e) => e.name === "mysfc");

            if (userInfo && hasPermission) {
                const locale = String(userInfo.language).toLowerCase().startsWith("pt") ? "pt-BR" : "en-US";
                this.$i18n.locale = locale;

                this.$ability.update(userInfo.userData.ability);

                this.userEmail = userInfo.userData.email;
                this.prefix = company ?? "";

                const userSites = userInfo.userData?.sites || [];
                const firstSiteId = userSites[0]?.id || userSites[0]?.guid || userSites[0] || "";
                const hasValidCurrentSite = userSites.some((s) => (s.id || s.guid || s) == userInfo?.currentSite);

                let activeSite = userInfo?.currentSite;
                if (!hasValidCurrentSite || !activeSite) {
                    activeSite = firstSiteId;
                    this.updateUserInfoCookie({ currentSite: activeSite });
                }
                localStorage.setItem("currentSite", activeSite);

                localStorage.setItem("accessToken", userInfo.temporaryToken);
                localStorage.setItem("userData", JSON.stringify(userInfo.userData));
                localStorage.setItem("prefix", this.prefix);
                localStorage.setItem("sites", JSON.stringify(userInfo.userData.sites));

                const infoAccount = {
                    guid: userInfo.userData.guid,
                    employee_id: userInfo.userData.employee_id,
                    first_name: userInfo.userData.first_name,
                    last_name: userInfo.userData.last_name,
                    job_role: userInfo.userData.job_role,
                    photo: userInfo.userData.photo
                };

                if (localStorage.getItem("loggedAccountsInfo")) {
                    if (!JSON.parse(localStorage.getItem("loggedAccountsInfo")).find(item => item.guid == userInfo.userData.guid)) {
                        var guidAtual = JSON.parse(localStorage.getItem("loggedAccountsInfo"));
                        guidAtual.unshift(infoAccount);
                        localStorage.setItem("loggedAccountsInfo", JSON.stringify(guidAtual));
                    }
                } else {
                    localStorage.setItem("loggedAccountsInfo", JSON.stringify([infoAccount]));
                }

                const pcpAccess = userInfo.userData.ability.find(e => e.action == "manage") ||
                    userInfo.userData.ability.find(e => e.subject == "pcp");

                const isOperatorPage = window.location.pathname.includes("operator") || window.location.pathname.includes("change-account");

                if (!pcpAccess && v && !isOperatorPage) {
                    this.$router.push({ name: "operator", params: { prefix: this.prefix } });
                }
                if (pcpAccess && v && !isOperatorPage) {
                    this.$router.push({ name: "home", params: { prefix: this.prefix } });
                }
            } else {
                this.$emit("logout");
                window.location.href = `${this.$myIndLink}login`;
            }
        },

        verifyCanNavigate(to) {
            const permissions = this.getPermissions;

            if (!permissions || permissions.length === 0) return;

            let pcpAccess = false;
            let operatorAccess = false;

            const mysfc = permissions.find((perm) => perm.name === "mysfc");
            if (mysfc) {
                const pcp = mysfc.children.find((child) => child.name === "pcp");
                const operator = mysfc.children.find((child) => child.name === "operator");

                pcpAccess = !!(pcp && ((pcp.options && pcp.options.length > 0) || (pcp.children && pcp.children.length > 0)));
                operatorAccess = !!(operator && ((operator.options && operator.options.length > 0) || (operator.children && operator.children.length > 0)));
            }

            const isPcpUrl = to.meta.resource === "pcp" || to.meta.resource === "basic";
            const isOperatorUrl = to.meta.resource === "operator" || to.meta.resource === "basic";
            const hasBothPermissions = operatorAccess && pcpAccess;

            if (pcpAccess && !isPcpUrl && !hasBothPermissions) {
                return this.$router.replace({
                    name: "home",
                    params: { prefix: localStorage.getItem("prefix") }
                }).catch(() => { });
            }

            if (operatorAccess && !isOperatorUrl && !hasBothPermissions) {
                return this.$router.replace({
                    name: "operator",
                    params: { prefix: localStorage.getItem("prefix") }
                }).catch(() => { });
            }
        },

        handleCreateAbility(permissions) {
            const mySfcModules = permissions?.find((system) => system.name.toLowerCase() === "mysfc")?.children ?? [];

            return [];
        },

        redirect(name, params) {
            const isSamePage = String(this.$router.currentRoute.name) === String(name);
            if (!isSamePage) {
                this.$router.push({ name, params });
            }
        },
        inactivityTime() {
            const isOperatorPage = this.$router.currentRoute.path.includes("operator");
            if (isOperatorPage) {
                window.addEventListener("mousemove", this.resetInactivityTime);
                window.addEventListener("touchend", this.resetInactivityTime);
                window.addEventListener("touchmove", this.resetInactivityTime);
                window.addEventListener("keydown", this.resetInactivityTime);
                this.resetInactivityTime();
                return;
            }
            window.removeEventListener("mousemove", this.resetInactivityTime);
            window.removeEventListener("touchend", this.resetInactivityTime);
            window.removeEventListener("touchmove", this.resetInactivityTime);
            window.removeEventListener("keydown", this.resetInactivityTime);
            clearTimeout(this.timeoutFunction);
            clearTimeout(this.timeoutOverlay);
        },
        resetInactivityTime() {
            clearTimeout(this.timeoutFunction);
            clearTimeout(this.timeoutOverlay);
            const requestList = JSON.parse(localStorage.getItem("requestList"));
            if (requestList && requestList.length > 0) return;

            let milliseconds = Number(localStorage.getItem("time_to_lock")) * 1000;
            const isDev = window.location.hostname.includes("dev") || window.location.hostname.includes("localhost");

            if (localStorage.getItem("modal") === "off" && isDev)
                this.timeoutOverlay = localStorage.getItem("blockScreen") === "true" && this.flagOverlayModalPin
                    ? setTimeout(() => this.addOverlayModalPinAndOverlayNavbar(), milliseconds) : null;

            if (localStorage.getItem("blockScreen") === "true") return;

            $("body").removeClass("stop-scrolling");

            this.timeoutFunction = setTimeout(() => {
                if (!this.$router.currentRoute.path.includes("operator")) return;
                const requestListInTheTimeout = JSON.parse(localStorage.getItem("requestList"));
                if (requestListInTheTimeout.length > 0) return this.resetInactivityTime();

                localStorage.setItem("lastModule", localStorage.getItem("mode"));
                localStorage.setItem("blockScreen", true);
                localStorage.removeItem("accessToken");

                this.addOverlayModalPinAndOverlayNavbar();
                this.UPDATE_FLAG_SHOW_MODAL_PIN(true);
                $("body").addClass("stop-scrolling");
            }, milliseconds || 30000);
        },
        addOverlayModalPinAndOverlayNavbar() {
            this.UPDATE_FLAG_OVERLAY_MODAL_PIN(true);
            $(".header-navbar").addClass("overlay-navbar");
        }
    },
    setup() {
        const { skin, skinClasses } = useAppConfig();
        if (skin.value === "dark") document.body.classList.add("dark-layout");
        provideToast({
            hideProgressBar: true,
            closeOnClick: false,
            closeButton: false,
            icon: false,
            timeout: 3000,
            transition: "Vue-Toastification__fade"
        });
        store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
        const { width: windowWidth } = useWindowSize();
        watch(windowWidth, (val) => {
            store.commit("app/UPDATE_WINDOW_WIDTH", val);
        });

        const { connect } = provideSignalR();
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const token = localStorage.getItem("accessToken");
        if (userData.guid && token) {
            connect(userData.guid, token);
        }

        return { skinClasses };
    }
};
</script>

<style lang="scss">
body {
    &::-webkit-scrollbar {
        width: 7px;
    }

    &::-webkit-scrollbar-track {
        background: #fff;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #cfc4be;
        border-radius: 9px;
    }
}

.load-spinner-global {
    height: 50px !important;
    width: 50px !important;

    circle {
        fill: #974900 !important;
    }
}

.b-overlay {
    z-index: 9999999 !important;

    .position-absolute {
        &.bg-light {
            min-height: 100dvh;
            background: rgba(235, 235, 235, 0.8) !important;
            backdrop-filter: blur(20px) !important;
            opacity: 1 !important;
        }

        &:nth-child(2) {
            position: fixed !important;
            top: 50dvh !important;
            left: 0;
        }
    }
}

.overlay-navbar {
    opacity: 0.7;
}

.stop-scrolling {
    height: 100%;
    overflow: hidden !important;
}

* {
    font-family: "Raleway", sans-serif !important;
    font-feature-settings:
        "pnum" on,
        "lnum" on;
    font-style: normal;
}

.swal-default {
    z-index: 999999999 !important;

    .swal2-modal {
        padding: 0 !important;
        border-radius: 6px;
        width: 710px;

        .swal2-close {
            color: #4c4541 !important;
            font-size: 2em;
        }

        .swal2-header {
            background-color: #d32f2f;
            padding: 20px 16px;
            border-radius: 6px 6px 0px 0px;
            flex-direction: row;
            justify-content: left;

            .swal2-title {
                color: #ffff;
                font-weight: 600;
                font-size: 18px;
                line-height: 26px;
                margin: 0;
            }

            .swal2-close {
                border-radius: 6px;
                width: 34px;
                background-color: #ffff;
                height: 34px;
                top: -5px;
                position: absolute;
                right: -5px;
            }
        }

        .swal2-content {
            padding: 20px 16px;

            .swal2-html-container {
                font-weight: 400;
                font-size: 14px;
                text-align: left;
                line-height: 20px;
                color: #4c4541;
                display: flex !important;
            }
        }

        .swal2-actions {
            padding: 0 16px 20px 16px;
            display: flex;
            justify-content: right;
            margin-top: 0;

            .swal2-confirm {
                margin-left: 10px !important;
                order: 2;
                font-weight: 600;
                font-size: 17px;
                line-height: 20px;
                color: #ffff;
                border-color: #974900 !important;
                background-color: #974900 !important;
                border-radius: 5px;

                &:hover {
                    box-shadow: none !important;
                }
            }

            .swal2-cancel {
                order: 1;
                font-weight: 600;
                font-size: 17px;
                line-height: 20px;
                color: #974900 !important;
                border-radius: 5px;
                border-color: transparent !important;

                &:hover {
                    background-color: #ffede1 !important;
                }
            }
        }
    }
}
</style>
